import { useRoute } from 'vue-router';
import { useCatalogFilterTokens } from '@/composables/useCatalogFilterTokens';
import { CATALOG_CONFIG, DEFAULT_TRANSPORT } from '@/composables/useCatalogFilters';

const FILTER_PREFIX = {
  brandPrefix: 'tm-',
  sortPrefixes: ['sort-'],
  pricePrefix: 'price-',
};

// Inverted from CATALOG_CONFIG so the URL rules (which base path maps to
// which catalog type, whether it carries a transport segment) live in one
// place instead of being re-declared here.
const ROOT_TO_TYPE = Object.fromEntries(
  Object.entries(CATALOG_CONFIG).map(([type, cfg]) => [
    cfg.basePath.replace(/^\//, ''),
    type,
  ]),
);

// Word-break style check: can `remaining` be fully consumed by chaining
// tokens from `tokenList` separated by '-'? Used both for brand segments
// (tm-mercedes-benz-toyota -> ['mercedes-benz', 'toyota']) and for the
// other dash-joined multi-select segments (bodytype, transmission, ...).
function canBeDecomposed(remaining, tokenList) {
  if (remaining === '') return true;
  for (const token of tokenList) {
    if (remaining === token || remaining.startsWith(`${token}-`)) {
      if (canBeDecomposed(remaining.slice(token.length + 1), tokenList)) {
        return true;
      }
    }
  }
  return false;
}

/*
  Classifies a single URL segment against the catalog's filter grammar.
  Returns null when the segment isn't a recognized filter piece at all
  (garbage/scraped path fragment, unknown brand, made-up segment like
  "new"). Returns { field: 'transport', value } for a segment that is
  exactly one transport token, so the caller can reject the default value
  being spelled out in the URL. Any other recognized segment returns
  { field: '<field>' }.
*/
function classifySegment(seg, TOKENS, { hasTransport, isStock }) {
  if (seg.startsWith(FILTER_PREFIX.pricePrefix)) return { field: 'price' };
  if (FILTER_PREFIX.sortPrefixes.some((p) => seg.startsWith(p))) {
    return { field: 'sort' };
  }
  if (seg.startsWith(FILTER_PREFIX.brandPrefix)) {
    const rest = seg.slice(FILTER_PREFIX.brandPrefix.length);
    return canBeDecomposed(rest, TOKENS.brands || [])
      ? { field: 'brand' }
      : null;
  }
  if (hasTransport && (TOKENS.transport || []).includes(seg)) {
    return { field: 'transport', value: seg };
  }

  const otherTokens = [
    ...TOKENS.bodytype,
    ...TOKENS.transmission,
    ...TOKENS.fuel,
    ...TOKENS.wheelDrive,
    ...TOKENS.models,
    ...(isStock
      ? [...TOKENS.regions, ...TOKENS.colors, ...TOKENS.dealers]
      : []),
  ];
  return canBeDecomposed(seg, otherTokens) ? { field: 'other' } : null;
}

// Resolves the CMS slug for routes that don't need the async catalog-filter
// tokens (home, news, generic CMS pages) — returns null for catalog routes,
// which only getPageSlug() below can resolve (it has access to tokens).
// Pulled out as a pure, path-only function so server/plugins/prefetchCriticalData
// can prefetch page data before the Vue tree even starts rendering, without
// duplicating (and risking drift from) this route-classification logic.
export function getPageSlugFast(p) {
  if (p === '/' || p === '/en' || p === '/en/') {
    return `/pages/home`;
  }

  const path = p.replace(/^\/en(\/|$)/, '/');
  const segments = path.split('/').filter(Boolean);
  const [root, ...rest] = segments;

  const isNewsPath =
    root === 'pres-centr' || (root === 'prescentr' && rest[0] === 'news');

  if (isNewsPath) {
    return `/pages/news`;
  }

  if (ROOT_TO_TYPE[root]) {
    return null;
  }

  const clean = path.replace(/^\//, '').replace(/\/$/, '');
  return `/pages/${clean || 'home'}`;
}

export function useApiSlugs() {
  const route = useRoute();
  const lang = route.params.lang || 'uk';
  const { tokens } = useCatalogFilterTokens();

  const getPageSlug = () => {
    const p = route.path;

    const fastSlug = getPageSlugFast(p);
    if (fastSlug !== null) {
      return fastSlug;
    }

    // fastSlug is null only when the root segment is a recognized catalog
    // type (see getPageSlugFast above) — resolving those needs the tokens
    // fetched below, which getPageSlugFast deliberately can't depend on.
    const path = p.replace(/^\/en(\/|$)/, '/');
    const segments = path.split('/').filter(Boolean);
    const [root, ...rest] = segments;
    const catalogType = ROOT_TO_TYPE[root];

    if (rest.length === 0) return `/pages/${root}`;

    const TOKENS = tokens?.value || {
      brands: [],
      transport: ['passenger', 'truck', 'commercial', 'moto', 'bus'],
      bodytype: [],
      transmission: [],
      fuel: [],
      wheelDrive: [],
      models: [],
      regions: [],
      colors: [],
      dealers: [],
    };
    const config = CATALOG_CONFIG[catalogType];
    const isStock = catalogType === 'stock';

    const classified = rest.map((seg) =>
      classifySegment(seg, TOKENS, {
        hasTransport: config.hasTransport,
        isStock,
      }),
    );

    const transportMatches = classified.filter(
      (c) => c?.field === 'transport',
    );
    const isValidCatalogPath =
      classified.every(Boolean) &&
      transportMatches.length <= 1 &&
      !transportMatches.some((c) => c.value === DEFAULT_TRANSPORT);

    if (isValidCatalogPath) {
      return `/pages/${root}`;
    }

    // A lone segment with no filter syntax at all (no tm-/price-/sort-
    // prefix, doesn't decompose into any known token) is a car detail
    // slug, e.g. /cars/some-model-slug — not an invalid filter combo.
    const hasFilterSyntax =
      rest[0].startsWith(FILTER_PREFIX.brandPrefix) ||
      rest[0].startsWith(FILTER_PREFIX.pricePrefix) ||
      FILTER_PREFIX.sortPrefixes.some((p) => rest[0].startsWith(p));

    if (rest.length === 1 && !classified[0] && !hasFilterSyntax) {
      return `/pages/car/${rest[0]}`;
    }

    throw createError({ statusCode: 404, statusMessage: 'Page not found' });
  };

  return { lang, getPageSlug };
}
